import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useFeatureToggle } from '../context/FeatureToggleContext';
import { useAuth } from '../context/AuthContext';
import { useClubBranding } from '../context/ClubBrandingContext';
import { Button } from './ui/button';
import { Dog, Globe, Search, Mail, User, Users } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { navGroups } from './nav/navConfig';
import DesktopAuthNav from './nav/parts/DesktopAuthNav';
import GuestNav from './nav/parts/GuestNav';
import MobileMenu from './nav/parts/MobileMenu';
import NavSearchBar from './nav/parts/NavSearchBar';
import UserMenu from './nav/parts/UserMenu';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { isEnabled } = useFeatureToggle();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const branding = useClubBranding();

  // Filter nav items by enabled features
  const filteredNavGroups = Object.fromEntries(
    Object.entries(navGroups).map(([key, group]) => [
      key,
      {
        ...group,
        items: group.items.filter((item) => !item.feature || isEnabled(item.feature)),
      },
    ])
  );

  // Flat guest-accessible list for the guest Explore menu
  const guestAccessibleItems = Object.values(filteredNavGroups)
    .flatMap((group) => group.items)
    .filter((item) => item.guestAccessible);

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (group) => group.items.some((item) => location.pathname === item.href);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navBg = branding.navColor || '#78350f'; // amber-900 default
  const isDarkNav = true; // stone-800 / emerald-900 / deep red are all dark — always use light text

  return (
    <header
      className="sticky top-0 z-50 border-b border-white/10 shadow-sm"
      style={{ backgroundColor: navBg }}
      data-testid="site-navbar"
    >
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            {branding.logoUrlSmall || branding.logoUrl ? (
              <img
                src={branding.logoUrlSmall || branding.logoUrl}
                alt={branding.logoAlt || `${branding.siteName} ${branding.siteNameSecondary || ''}`.trim() + ' logo'}
                className="w-10 h-10 rounded-full object-cover group-hover:scale-105 transition-transform bg-transparent ring-2 ring-white/20"
                data-testid="nav-logo-img"
              />
            ) : (
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center group-hover:scale-105 transition-transform ${
                  branding.isWorldPets ? 'bg-emerald-600' : 'bg-amber-500'
                }`}
              >
                {branding.isWorldPets ? (
                  <Globe className="w-5 h-5 text-white" />
                ) : (
                  <Dog className="w-5 h-5 text-white" />
                )}
              </div>
            )}
            <span className={`font-heading text-xl font-semibold hidden sm:block ${isDarkNav ? 'text-white' : ''}`}>
              {branding.siteName}
              <span className={branding.isWorldPets ? 'text-emerald-300' : 'text-amber-300'}>
                {' '}
                {branding.siteNameSecondary}
              </span>
            </span>
          </Link>

          {/* Desktop nav (authed) */}
          {user && (
            <DesktopAuthNav
              filteredNavGroups={filteredNavGroups}
              isActive={isActive}
              isGroupActive={isGroupActive}
            />
          )}

          {/* Right section */}
          <div className="flex items-center gap-2">
            {/* Global search bar — visible on desktop, replaces the icon-only affordance */}
            <NavSearchBar />

            <div className="hidden md:block">
              <LanguageSelector variant="compact" />
            </div>

            {user ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden md:flex text-white hover:bg-white/10 hover:text-white"
                  onClick={() => navigate('/communities')}
                  data-testid="nav-communities-direct"
                  title="Communities"
                >
                  <Users className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:flex lg:hidden text-white hover:bg-white/10 hover:text-white"
                  onClick={() => navigate('/search')}
                  data-testid="nav-search"
                  title="Search"
                  aria-label="Search"
                >
                  <Search className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden md:flex text-white hover:bg-white/10 hover:text-white"
                  onClick={() => navigate('/messages')}
                  data-testid="nav-messages"
                >
                  <Mail className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden md:flex text-white hover:bg-white/10 hover:text-white"
                  onClick={() => navigate(`/profile/${user.username}`)}
                  data-testid="nav-profile-direct"
                >
                  <User className="w-5 h-5" />
                </Button>

                <UserMenu user={user} onLogout={handleLogout} />

                <MobileMenu
                  user={user}
                  filteredNavGroups={filteredNavGroups}
                  open={mobileOpen}
                  setOpen={setMobileOpen}
                  isActive={isActive}
                />
              </>
            ) : (
              <GuestNav
                guestAccessibleItems={guestAccessibleItems}
                mobileOpen={mobileOpen}
                setMobileOpen={setMobileOpen}
                isActive={isActive}
              />
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
